import { Injectable } from "@angular/core";
import { GuestOwnerBootstrapResponse, GuestOwnerPlayerDto, GuestOwnerSessionState } from "../models/guestOwner";

@Injectable()
export class GuestOwnerSessionService {
  public static readonly INSTALLATION_ID_KEY = "nogosh.guestOwner.installationId";
  public static readonly SESSION_KEY = "nogosh.guestOwner.session";

  public getInstallationId(): string {
    let installationId = localStorage.getItem(GuestOwnerSessionService.INSTALLATION_ID_KEY);
    if (!installationId) {
      installationId = this.createInstallationId();
      localStorage.setItem(GuestOwnerSessionService.INSTALLATION_ID_KEY, installationId);
    }
    return installationId;
  }

  public getSession(): GuestOwnerSessionState {
    const rawSession = localStorage.getItem(GuestOwnerSessionService.SESSION_KEY);
    if (!rawSession) {
      return null;
    }

    try {
      return JSON.parse(rawSession);
    } catch (error) {
      localStorage.removeItem(GuestOwnerSessionService.SESSION_KEY);
      return null;
    }
  }

  public saveBootstrap(response: GuestOwnerBootstrapResponse): GuestOwnerSessionState {
    const state: GuestOwnerSessionState = {
      installationId: this.getInstallationId(),
      guestOwnerToken: response.guestOwnerToken,
      player: response.player
    };
    this.saveSession(state);
    return state;
  }

  public saveInvite(inviteToken: string, invitePath: string): GuestOwnerSessionState {
    const state = this.getSession();
    if (!state) {
      return null;
    }

    state.inviteToken = inviteToken;
    state.invitePath = invitePath;
    this.saveSession(state);
    return state;
  }

  public updatePlayer(player: GuestOwnerPlayerDto): GuestOwnerSessionState {
    const state = this.getSession();
    if (!state) {
      return null;
    }

    state.player = player;
    this.saveSession(state);
    return state;
  }

  public saveCurrentEvent(eventId: string, eventName: string): GuestOwnerSessionState {
    const state = this.getSession();
    if (!state) {
      return null;
    }

    state.eventId = eventId;
    state.eventName = (eventName || "").trim();
    this.saveSession(state);
    return state;
  }

  public clearCurrentEvent(): GuestOwnerSessionState {
    const state = this.getSession();
    if (!state) {
      return null;
    }

    delete state.eventId;
    delete state.eventName;
    this.saveSession(state);
    return state;
  }

  public clearInvite(): GuestOwnerSessionState {
    const state = this.getSession();
    if (!state) {
      return null;
    }

    delete state.inviteToken;
    delete state.invitePath;
    this.saveSession(state);
    return state;
  }

  public clearSession(): void {
    localStorage.removeItem(GuestOwnerSessionService.SESSION_KEY);
  }

  private saveSession(state: GuestOwnerSessionState): void {
    localStorage.setItem(GuestOwnerSessionService.SESSION_KEY, JSON.stringify(state));
  }

  private createInstallationId(): string {
    if (window.crypto && window.crypto.getRandomValues) {
      const values = new Uint32Array(4);
      window.crypto.getRandomValues(values);
      return Array.from(values).map((value) => value.toString(16)).join("-");
    }

    return "web-" + new Date().getTime() + "-" + Math.random().toString(16).slice(2);
  }
}
